/**
 * Utility functions for handling authentication errors
 */

/**
 * Check if an error indicates that a user already exists
 */
export function isExistingAccountError(error: any): boolean {
  if (!error?.message) return false;
  
  const errorMessage = error.message.toLowerCase();
  return errorMessage.includes('already exists') ||
         errorMessage.includes('already registered') ||
         errorMessage.includes('user already registered');
}

/**
 * Check if a user already exists by email
 * This is useful for OAuth flows where the error might not be immediately apparent
 */
export async function checkUserExists(email: string): Promise<boolean> {
  try {
    // Import supabase client dynamically to avoid circular dependencies
    const { supabase } = await import('@/integrations/supabase/client');
    
    // Try to sign in with a dummy password to see if the account exists
    // This will fail with "Invalid login credentials" if the account doesn't exist
    // or with a different error if it does exist
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password: 'dummy-password-for-check'
    });
    
    // If we get an error about invalid credentials, the account likely exists
    // If we get an error about user not found, the account doesn't exist
    if (error) {
      const errorMessage = error.message.toLowerCase();
      return errorMessage.includes('invalid login credentials') ||
             errorMessage.includes('invalid email or password') ||
             errorMessage.includes('user not found') === false; // If it's NOT "user not found", account exists
    }
    
    return false;
  } catch (error) {
    console.error('Error checking if user exists:', error);
    return false;
  }
}

/**
 * Get a user-friendly error message for authentication errors
 */
export function getAuthErrorMessage(error: any): string {
  if (isExistingAccountError(error)) {
    return "You already have an account with this email. Please sign in instead.";
  }
  
  return error?.message || "Authentication failed. Please try again.";
}

/**
 * Get a user-friendly error title for authentication errors
 */
export function getAuthErrorTitle(error: any): string {
  if (isExistingAccountError(error)) {
    return "Account Already Exists";
  }
  
  return "Authentication Failed";
} 